import { useEffect, useRef, useState } from "react";
import {
  useGlobalOverlayBasePath,
  useRoutedDialog,
} from "shared-ui/src/hooks/routedOverlays";
import type CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import useAddShoppingCartProduct, {
  type AddShoppingCartProductInput,
} from "@features/ecommerce/hooks/useAddShoppingCartProduct";
import type { AddToCartModalProps } from "@features/ecommerce/cart/AddToCartModal";
import { ADDED_TO_CART_DIALOG_SEGMENT } from "@features/ecommerce/constants/cartRouteSegments";

export interface AddToCartModalOptions {
  onSuccess?: () => void;
}

/**
 * Wires the single-item add-to-cart mutation to the confirmation modal (SPU-534).
 *
 * On a successful add it captures the just-added product and opens the modal. Config
 * products go through a separate Configure flow and never reach this path, so they are
 * inherently excluded from the confirmation modal.
 *
 * The modal is routed as a transient `{page}/added-to-cart` dialog so it records a page view
 * and closes on Back. Several instances can be mounted on one page (a grid plus a drawer),
 * so only the instance that captured the product renders it as open.
 */
export default function useAddToCartModal() {
  const basePath = useGlobalOverlayBasePath(ADDED_TO_CART_DIALOG_SEGMENT);
  const dialog = useRoutedDialog({
    path: ADDED_TO_CART_DIALOG_SEGMENT,
    basePath,
    transient: true,
  });
  const [product, setProduct] = useState<CatalogedProduct | null>(null);
  const [quantity, setQuantity] = useState(1);
  const { mutate, isLoading } = useAddShoppingCartProduct();

  const sourceTriggerRef = useRef<HTMLElement | null>(null);

  const showModal = (
    candidate: AddShoppingCartProductInput,
    sourceTrigger?: HTMLElement | null,
  ) => {
    sourceTriggerRef.current = sourceTrigger ?? null;
    setProduct(candidate);
    setQuantity(
      candidate.quantity && candidate.quantity > 0 ? candidate.quantity : 1,
    );
    dialog.open();
  };

  const addToCart = (
    candidate: AddShoppingCartProductInput,
    options?: AddToCartModalOptions,
  ) => {
    mutate(candidate, {
      onSuccess: () => {
        showModal(candidate);
        options?.onSuccess?.();
      },
    });
  };

  const closeModal = () => {
    dialog.close();
  };

  // Clears the product and restores focus however the dialog closed (button or Back).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (dialog.isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) {
      return;
    }
    wasOpenRef.current = false;
    setProduct(null);
    const triggerToFocus = sourceTriggerRef.current;
    sourceTriggerRef.current = null;
    if (!triggerToFocus?.isConnected) {
      return;
    }
    requestAnimationFrame(() => {
      triggerToFocus.focus();
    });
  }, [dialog.isOpen]);

  const modalProps: Pick<
    AddToCartModalProps,
    "open" | "product" | "quantity" | "onClose"
  > = {
    open: dialog.isOpen && product != null,
    product,
    quantity,
    onClose: closeModal,
  };

  return { addToCart, showModal, isAdding: isLoading, modalProps };
}
